import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import PageTransition from '../components/ui/PageTransition';
import Button from '../components/ui/Button';
import { getArticleBySlug, articles } from '../data/articles';

const ArticlePage = () => {
  const { slug } = useParams<{ slug: string }>();
  const article = getArticleBySlug(slug || '');
  
  if (!article) {
    return (
      <PageTransition>
        <div className="py-32 text-center">
          <h1 className="text-3xl font-bold text-primary mb-4">Article Not Found</h1>
          <p className="text-secondary mb-8">The article you are looking for does not exist or has been moved.</p>
          <Button to="/advice" variant="primary">
            Back to Articles
          </Button>
        </div>
      </PageTransition>
    );
  }

  // Get related articles from the same category
  const relatedArticles = articles
    .filter(a => a.id !== article.id && a.category === article.category)
    .slice(0, 3);

  // Get previous and next articles
  const currentIndex = articles.findIndex(a => a.id === article.id);
  const previousArticle = currentIndex > 0 ? articles[currentIndex - 1] : null;
  const nextArticle = currentIndex < articles.length - 1 ? articles[currentIndex + 1] : null;

  // Format the date for display
  const formattedDate = new Date(article.date).toLocaleDateString('en-GB', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  return (
    <PageTransition>
      <Helmet>
        <title>{article.title} | Consiliuma Web Design & Development</title>
        <meta name="description" content={article.excerpt} />
        <meta name="keywords" content={`${article.category}, Web Design Tips, Development Guide, ${article.title.toLowerCase()}`} />
        <link rel="canonical" href={`https://consiliuma.co.uk/advice/${article.slug}`} />

        {/* Open Graph Tags */}
        <meta property="og:title" content={article.title} />
        <meta property="og:description" content={article.excerpt} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={`https://consiliuma.co.uk/advice/${article.slug}`} />
        <meta property="og:image" content={article.imageUrl} />
        <meta property="og:site_name" content="Consiliuma" />
        <meta property="og:locale" content="en_GB" />
        <meta property="article:published_time" content={article.date} />
        <meta property="article:section" content={article.category} />
        <meta property="article:author" content="Consiliuma" />

        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={article.title} />
        <meta name="twitter:description" content={article.excerpt} />
        <meta name="twitter:image" content={article.imageUrl} />

        {/* Schema.org data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": `https://consiliuma.co.uk/advice/${article.slug}`
            },
            "headline": article.title,
            "description": article.excerpt,
            "image": article.imageUrl,
            "datePublished": article.date,
            "dateModified": article.date,
            "author": {
              "@type": "Organization",
              "name": "Consiliuma",
              "url": "https://consiliuma.co.uk"
            },
            "publisher": {
              "@type": "Organization",
              "name": "Consiliuma",
              "logo": {
                "@type": "ImageObject",
                "url": "https://consiliuma.co.uk/favicon.svg"
              }
            },
            "articleSection": article.category,
            "articleBody": article.content.replace(/<[^>]*>/g, '')
          })}
        </script>
      </Helmet>

      <article className="pt-24 pb-16">
        <div className="container-custom">
          <nav className="mb-6" aria-label="Breadcrumb">
            <Link 
              to="/advice" 
              className="inline-flex items-center text-primary hover:text-primary-dark transition-colors"
              aria-label="Back to Articles"
            >
              <ArrowLeft size={20} className="mr-2" aria-hidden="true" />
              Back to Articles
            </Link>
          </nav>
          
          <header className="mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-primary mb-6">{article.title}</h1>
            <div className="flex items-center text-gray-600 mb-8">
              <time dateTime={article.date}>{formattedDate}</time>
              <span className="mx-2" aria-hidden="true">•</span>
              <span>{article.category}</span>
            </div>
            
            <div className="w-full h-64 md:h-96 rounded-lg overflow-hidden">
              <img 
                src={article.imageUrl} 
                alt={article.title}
                className="w-full h-full object-cover"
                loading="eager"
              />
            </div>
          </header>
        </div>
        
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            <div className="lg:col-span-2">
              <motion.div 
                className="prose prose-lg max-w-none"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
              >
                <div 
                  className="article-content" 
                  dangerouslySetInnerHTML={{ __html: article.content }}
                />
              </motion.div>
              
              <nav 
                className="mt-12 pt-8 border-t border-gray-200"
                aria-label="Article navigation"
              >
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                  {previousArticle ? (
                    <Link 
                      to={`/advice/${previousArticle.slug}`}
                      className="group flex items-center text-primary hover:text-primary-dark transition-colors"
                      aria-label={`Previous article: ${previousArticle.title}`}
                    >
                      <ArrowLeft 
                        size={20} 
                        className="mr-2 transition-transform group-hover:-translate-x-1" 
                        aria-hidden="true"
                      />
                      <div>
                        <div className="text-sm text-gray-500">Previous Article</div>
                        <div className="font-semibold">{previousArticle.title}</div>
                      </div>
                    </Link>
                  ) : (
                    <div /> // Empty div for spacing when no previous article
                  )}

                  {nextArticle && (
                    <Link 
                      to={`/advice/${nextArticle.slug}`}
                      className="group flex items-center text-right text-primary hover:text-primary-dark transition-colors ml-auto"
                      aria-label={`Next article: ${nextArticle.title}`}
                    >
                      <div>
                        <div className="text-sm text-gray-500">Next Article</div>
                        <div className="font-semibold">{nextArticle.title}</div>
                      </div>
                      <ArrowRight 
                        size={20} 
                        className="ml-2 transition-transform group-hover:translate-x-1" 
                        aria-hidden="true"
                      />
                    </Link>
                  )}
                </div>
              </nav>
            </div>
            
            <aside>
              <div className="bg-background-alt p-6 rounded-lg sticky top-24">
                <h2 className="text-xl font-bold text-primary mb-6">Related Articles in {article.category}</h2>
                
                {relatedArticles.length > 0 ? (
                  <div className="space-y-6">
                    {relatedArticles.map(relatedArticle => (
                      <div key={relatedArticle.id} className="group">
                        <Link 
                          to={`/advice/${relatedArticle.slug}`}
                          className="block"
                          aria-label={relatedArticle.title}
                        >
                          <div className="aspect-video rounded-md overflow-hidden mb-3">
                            <img 
                              src={relatedArticle.imageUrl} 
                              alt={relatedArticle.title} 
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                              loading="lazy"
                            />
                          </div>
                          <h3 className="font-bold text-primary group-hover:text-primary-dark transition-colors">
                            {relatedArticle.title}
                          </h3>
                          <p className="text-sm text-gray-600 mt-2 line-clamp-2">{relatedArticle.excerpt}</p>
                        </Link>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-600">No related articles found in {article.category}.</p>
                )}
                
                <div className="mt-8">
                  <Button to="/advice" variant="outline" className="w-full">
                    View All Articles
                  </Button>
                </div>
              </div>
            </aside>
          </div>
        </div>
      </article>
      
      <div className="py-16 bg-primary text-white">
        <div className="container-custom text-center">
          <h2 className="text-3xl font-bold mb-6">Need Help With Your Website?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Our team of British experts is ready to help with your web design and development needs.
          </p>
          <Button to="/contact" variant="outline" className="border-white text-white hover:bg-white hover:text-primary">
            Get in Touch
          </Button>
        </div>
      </div>
    </PageTransition>
  );
};

export default ArticlePage;